// PremiumTestsPage.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { FaChartLine, FaCalendarAlt, FaTrophy, FaHistory, FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const testTypes = [
  {
    name: "PRACTICE TEST",
    icon: <FaChartLine className="text-2xl" />,
    color: "from-green-500 to-teal-400",
    description: "Practice specific topics at your own pace",
    route: "/practice-test"
  },
  {
    name: "WEEKLY TEST",
    icon: <FaCalendarAlt className="text-2xl" />,
    color: "from-blue-500 to-indigo-400",
    description: "Weekly assessments to track progress",
    route: "/weekly-test"
  },
  {
    name: "MEGA TEST",
    icon: <FaTrophy className="text-2xl" />,
    color: "from-purple-500 to-pink-400",
    description: "Comprehensive exam simulation",
    route: "/mega-test"
  }
];

const PremiumTestsPage = () => {
  const navigate = useNavigate();
  const [testHistory, setTestHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testAccessStatus, setTestAccessStatus] = useState({
    hasSubscription: false,
    isApproved: false
  });
  const [studentId, setStudentId] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      const userData = localStorage.getItem("user");
      if (userData) {
        const user = JSON.parse(userData);
        setStudentId(user.id);
        
        try {
          // Fetch test subscription status
          const testResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/payment/test-subscription-status/${user.id}`
          );
          const testData = await testResponse.json();
          setTestAccessStatus(testData);

          // Fetch test history
          const historyResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/auth/student/test-history/${user.id}`
          );
          const historyData = await historyResponse.json();
          const premiumHistory = historyData.filter(test => 
            ['practice', 'weekly', 'mega'].includes(test.testType)
          );
          setTestHistory(premiumHistory);
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, []);

  const handleSubscriptionPayment = async (e) => {
    e.preventDefault();
    const stripe = await stripePromise;
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payment/create-subscription-session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ student_id: studentId }),
    });
    const session = await response.json();
    const result = await stripe.redirectToCheckout({ sessionId: session.id });
    if (result.error) alert(result.error.message);
  };

  const formatTestDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getTestTypeName = (testType) => {
    switch(testType) {
      case 'practice': return 'Practice Test';
      case 'weekly': return 'Weekly Test';
      case 'mega': return 'Mega Test';
      default: return 'Test';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 to-teal-50 min-h-screen py-12 px-6 md:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center text-blue-600 hover:text-blue-800"
          >
            <FaChevronLeft className="mr-1" /> Back to Dashboard
          </button>
          <h2 className="text-3xl font-bold text-gray-800 mt-4">Premium Tests</h2>
          <p className="text-gray-600">Access all premium test types</p>
          
          {testAccessStatus.hasSubscription && !testAccessStatus.isApproved && (
            <div className="bg-yellow-100 text-yellow-800 p-3 rounded-lg mt-4">
              Your subscription is pending admin approval
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {testTypes.map((test, index) => (
            <div 
              key={index}
              className={`p-[2px] rounded-2xl bg-gradient-to-br ${test.color} shadow-md hover:shadow-lg transition`}
            >
              <div className="bg-white rounded-2xl p-6 h-full flex flex-col">
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-full bg-gradient-to-br from-white to-gray-100 mr-4">
                    {test.icon}
                  </div>
                  <h3 className="text-xl font-bold">{test.name}</h3>
                </div>
                <p className="text-gray-600 mb-6 flex-grow">{test.description}</p>
                <button
                  onClick={() => {
                    if (testAccessStatus.hasSubscription && testAccessStatus.isApproved) {
                      navigate(test.route);
                    } else {
                      alert("You need an active premium subscription to access this test");
                    }
                  }}
                  className={`w-full py-3 rounded-lg font-semibold text-white bg-gradient-to-br ${test.color} hover:opacity-90 transition ${
                    !(testAccessStatus.hasSubscription && testAccessStatus.isApproved) ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  Start {test.name}
                </button>
              </div>
            </div>
          ))}
        </div>

        {!testAccessStatus.hasSubscription && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Get Premium Access</h3>
            <p className="text-gray-600 mb-4">Subscribe to unlock all premium test types</p>
            <button
              onClick={handleSubscriptionPayment}
              className="w-full py-3 rounded-lg font-semibold text-white bg-gradient-to-br from-yellow-500 to-orange-400 hover:opacity-90 transition"
            >
              Subscribe Now
            </button>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-gray-800 flex items-center">
              <FaHistory className="mr-2 text-blue-500" /> Your Test History
            </h3>
            <button 
              onClick={() => navigate('/test-history', { state: { filter: 'premium' } })}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              View All <FaChevronRight className="inline ml-1" />
            </button>
          </div>
          
          {testHistory.length > 0 ? (
            <div className="space-y-4">
              {testHistory.slice(0, 3).map((test, index) => (
                <div 
                  key={index}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/test-results/${test.testType}/${test.testId}`)}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-bold text-gray-800">{getTestTypeName(test.testType)}</h4>
                      <p className="text-sm text-gray-500">{formatTestDate(test.completedAt)}</p>
                    </div>
                    <div className="flex items-center">
                      <div style={{ width: 40, height: 40, marginRight: 10 }}>
                        <CircularProgressbar
                          value={test.scorePercentage}
                          text=""
                          styles={buildStyles({
                            pathColor: test.scorePercentage >= 70 ? '#10B981' : 
                                      test.scorePercentage >= 40 ? '#F59E0B' : '#EF4444',
                            trailColor: '#E5E7EB',
                          })}
                        />
                      </div>
                      <span className={`font-medium ${
                        test.scorePercentage >= 70 ? 'text-green-600' : 
                        test.scorePercentage >= 40 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {test.scorePercentage}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No test history available</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PremiumTestsPage;